'use client';

import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ScrollShadow,
  useDisclosure,
} from '@nextui-org/react';
import { SearchIcon } from 'lucide-react';
import NextImage from 'next/image';
import React, { useEffect, useMemo, useRef } from 'react';
import { useGetAllMasterclassesQuery } from '@/entities/masterclass/query';
import { IMasterclass } from '@/entities/masterclass/types';
import { LAZY_IMAGE_LOADING } from '@/shared/const/image-props';

import { debounce } from 'lodash-es';
import Link from 'next/link';
import { Spinner } from '@nextui-org/spinner';
import { isSERVER } from '@/entities/cart/utils';

const useGetLocalStorageSearchValue = () => {
  const [searchValue, setSearchValue] = React.useState([]);
  useEffect(() => {
    if (isSERVER) return;
    const value = JSON.parse(localStorage.getItem('searchValue') || '[]');
    setSearchValue(value);
  }, []);
  return searchValue;
};

export const Search = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchValue, setSearchValue] = React.useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = React.useState('');
  const { data: masterclasses } = useGetAllMasterclassesQuery({
    enabled: isOpen,
  });
  const inputRef = useRef(null);
  const [loading, setLoading] = React.useState(false);
  const prevSearchValue = useGetLocalStorageSearchValue();
  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchValue(value);
        setLoading(true);
        setTimeout(() => {
          setLoading(false);
        }, 500);
      }, 500),
    [],
  );

  const filteredData = useMemo(() => {
    if (!debouncedSearchValue || !masterclasses) {
      return [];
    }

    const prev = prevSearchValue || [];
    const data = Array.from(new Set([debouncedSearchValue, ...prev])).splice(
      0,
      10,
    );
    localStorage.setItem('searchValue', JSON.stringify(data));
    return masterclasses?.filter((mc: IMasterclass) =>
      mc.name.toLowerCase().includes(debouncedSearchValue.toLowerCase()),
    );
  }, [debouncedSearchValue, prevSearchValue, masterclasses]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    (inputRef.current as unknown as HTMLInputElement)?.focus();
  }, [isOpen]);

  return (
    <>
      <Button
        className="md:hidden flex"
        variant="light"
        radius="full"
        isIconOnly
        onClick={onOpen}
        aria-label="Поиск"
      >
        <SearchIcon size={24} strokeWidth={1} />
      </Button>
      <button
        onClick={onOpen}
        className="z-0 w-[14rem] rounded-full hidden md:flex group relative items-center box-border appearance-none select-none whitespace-nowrap subpixel-antialiased overflow-hidden tap-highlight-transparent data-[pressed=true]:scale-[0.97] outline-none data-[focus-visible=true]:z-10 data-[focus-visible=true]:outline-2 data-[focus-visible=true]:outline-focus data-[focus-visible=true]:outline-offset-2 px-4 min-w-20 h-10 gap-2 justify-start [&amp;>svg]:max-w-[theme(spacing.8)] transition-transform-colors-opacity motion-reduce:transition-none data-[hover=true]:opacity-hover text-sm font-normal text-default-700 bg-default-400/20 dark:bg-default-500/20"
        type="button"
        aria-label="Quick search"
      >
        <SearchIcon size={24} strokeWidth={1} />
        Поиск...
      </button>
      <Modal className="p-0" backdrop="blur" isOpen={isOpen} onClose={onClose}>
        <ModalContent>
          <>
            <ModalHeader className="flex flex-col gap-1 pb-0 pl-2">
              <div className="flex items-center w-full">
                <Input
                  ref={inputRef}
                  value={searchValue}
                  onChange={(e) => {
                    setSearchValue(e.target.value);
                    debouncedSearch(e.target.value);
                  }}
                  placeholder="Поиск..."
                  radius="full"
                  startContent={<SearchIcon size={24} strokeWidth={1} />}
                />
              </div>
            </ModalHeader>
            <ModalBody className="px-0">
              <ScrollShadow className="md:h-[400px] h-[300px]">
                {prevSearchValue.length > 0 && (
                  <div className="flex pl-3 flex-col gap-1 pb-2 ">
                    <div>Ранее вы искали:</div>
                    <div className="flex flex-wrap gap-1 items-center">
                      {prevSearchValue?.map((value: string) => (
                        <span
                          onClick={() => {
                            setSearchValue(value);
                            setDebouncedSearchValue(value);
                          }}
                          key={value}
                          className="bg-default-400/10 min-w-[30px] flex justify-center text-default-700 px-2 py-1   rounded-full cursor-pointer hover:bg-default-400/20"
                        >
                          {value}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {loading ? (
                  <div className="flex h-[200px] md:h-[300px] text-lg text-gray-900 font-light items-center justify-center">
                    <Spinner size="lg" />
                  </div>
                ) : (
                  <>
                    {!filteredData?.length && debouncedSearchValue && (
                      <div className="flex h-[200px] md:h-[300px] text-lg text-gray-900 font-light items-center justify-center">
                        Ничего не найдено
                      </div>
                    )}
                    {!debouncedSearchValue && (
                      <div className="flex h-[200px] md:h-[300px] flex-col text-lg text-gray-900 font-light items-center justify-center">
                        Введите запрос
                      </div>
                    )}
                    <div className="flex flex-col gap-4">
                      {filteredData?.map((mc: IMasterclass) => (
                        <Link
                          href={`/masterclasses/${mc.wordId}`}
                          key={mc.id}
                          className="w-full flex gap-3 items-center hover:bg-default-400/10 cursor-pointer p-2 pl-4 rounded-lg"
                        >
                          <NextImage
                            loading={LAZY_IMAGE_LOADING}
                            alt={mc.name}
                            className="object-cover"
                            src={mc.images?.[0].url}
                            width={100}
                            height={100}
                          />
                          <span className=" text-lg text-default-700">
                            {/* eslint-disable-next-line react/no-unescaped-entities */}
                            МК "{mc.name}"
                          </span>
                          <span className="text-green-500 text-lg ml-auto flex-shrink-0">
                            {' '}
                            {mc.price} ₽
                          </span>
                        </Link>
                      ))}
                    </div>
                  </>
                )}
              </ScrollShadow>
            </ModalBody>
          </>
        </ModalContent>
      </Modal>
    </>
  );
};
